/**
 * dateUtils.js
 * Funções utilitárias para conversão e formatação de datas
 */

/**
 * Converte um valor para um objeto Date, lidando com diferentes formatos
 * @param {*} value - Valor a ser convertido (string, timestamp, Date)
 * @param {Date|null} defaultValue - Valor padrão caso a conversão falhe
 * @returns {Date|null} - Objeto Date ou defaultValue
 */
export function parseDate(value, defaultValue = null) {
  if (!value) return defaultValue;

  // Se já for um objeto Date, retorna ele mesmo
  if (value instanceof Date) return value;

  let date = null;

  // Se for string, tenta converter
  if (typeof value === 'string') {
    // Tenta interpretar formatos comuns de data brasileira (dd/mm/aaaa)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [day, month, year] = value.split('/');
      date = new Date(`${year}-${month}-${day}`);
    }
    // Tenta interpretar formatos ISO (aaaa-mm-dd ou aaaa-mm-ddTHH:mm:ss)
    else if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      date = new Date(value);
    }
    // Tenta interpretar formato compacto aaaaMMDD
    else if (/^\d{8}$/.test(value)) {
      const year = value.slice(0, 4);
      const month = value.slice(4, 6);
      const day = value.slice(6, 8);
      date = new Date(`${year}-${month}-${day}`);
    }
    // Tenta interpretar outros formatos possíveis
    else {
      date = new Date(value);
    }
  }
  // Se for número (timestamp), converte
  else if (typeof value === 'number') {
    date = new Date(value);
  }

  // Verifica se a data é válida
  return date instanceof Date && !isNaN(date) ? date : defaultValue;
}

/**
 * Formata uma data para o padrão brasileiro (dd/mm/aaaa)
 * @param {*} value - Data a ser formatada (string, timestamp, Date)
 * @returns {string} - String formatada no padrão brasileiro ou string vazia
 */
export function formatDateBr(value) {
  const date = parseDate(value);
  if (!date) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Formata uma data com data e hora no padrão brasileiro
 * @param {*} value - Data a ser formatada (string, timestamp, Date)
 * @returns {string} - String formatada com data e hora ou string vazia
 */
export function formatDateTimeBr(value) {
  const date = parseDate(value);
  if (!date) return '';

  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Verifica se uma data está no passado
 * @param {*} value - Data a verificar
 * @returns {boolean} - true se a data estiver no passado
 */
export function isPastDate(value) {
  const date = parseDate(value);
  if (!date) return false;

  const now = new Date();
  return date < now;
}
